import React, { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { io } from "socket.io-client"
import { Tabs, Tab, Box } from "@mui/material"
import { MdInfoOutline } from "react-icons/md"
import { MobergFontSize } from "../../../../Components/MobergFont/MobergFont"
import {
    RemoteMonitoringModalContainer,
    RemoteMonitoringModalContent,
} from "../../../../Constants/StyledComponents"
import Header from "../../../RemoteMonitoring/components/Header"
import { useModalProvider } from "../../../../Providers/ModalProvider"
import { useEndpointProvider } from "../../../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../../../Providers/BackendLinksProvider"
import { useAuthProvider } from "../../../../Providers/AuthProvider"
import { useWorkspacesProvider } from "../../../../Providers/WorkspacesProvider"
import ExportsListView from "./ExportsListView"
import ExportsStorageMeter from "./ExportsStorageMeter"


// Socket.IO drives live updates; polling is the fallback for connection drops
// and reopen-while-converting races. 30s is intentional — see chunk 3.3 brief.
const POLL_INTERVAL_MS = 30000


// Plain info line — just the icon + text, no container/tint/border.
const InfoBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 30px 12px;
    font-family: 'Source Sans Pro';
    font-size: 12.5px;
    line-height: 1.4;
    color: var(--text-secondary-color);
`

const RefreshControls = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    color: var(--text-secondary-color);
    font-size: 12px;
`


export default function ExportsListModal() {
    const { close } = useModalProvider()
    const endpointProvider = useEndpointProvider()
    const { LINKS } = useBackendLinksProvider()
    const { token } = useAuthProvider()
    const { selectedWorkspace } = useWorkspacesProvider()

    const [activeTab, setActiveTab] = useState("recents")
    const [items, setItems] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [stats, setStats] = useState(null)   // { job_count, completed_count, total_bytes }
    const isMountedRef = useRef(true)

    const fetchStats = useCallback(async () => {
        try {
            const response = await endpointProvider.get(LINKS.DATA.EXPORTS.STATS, {})
            if (!isMountedRef.current) return
            setStats(response || null)
        } catch {
            // Stats are a polish-layer detail; swallow errors so a broken stats
            // endpoint doesn't break the modal as a whole.
        }
    }, [endpointProvider, LINKS])

    const fetchExports = useCallback(async () => {
        try {
            const response = await endpointProvider.get(
                LINKS.DATA.EXPORTS.LIST,
                { page: 1, page_size: 100 }
            )
            if (!isMountedRef.current) return
            const fetched = response?.items || []
            // Merge rather than replace: the worker only writes intermediate
            // progress over Socket.IO (DB only sees PENDING/COMPLETED/FAILED
            // terminal states). A naive replace here would wipe every live
            // IN_PROGRESS update back to progress=0 on each poll. Prefer the
            // existing in-memory row whenever the DB state is non-terminal.
            setItems(prev => {
                const prevById = new Map(prev.map(j => [j.id, j]))
                return fetched.map(serverItem => {
                    const existing = prevById.get(serverItem.id)
                    const terminal = serverItem.status === "COMPLETED"
                        || serverItem.status === "FAILED"
                        || serverItem.status === "CANCELLED"
                    if (existing && !terminal) {
                        return { ...serverItem, ...existing }
                    }
                    return serverItem
                })
            })
            setTotal(response?.total || 0)
            setError(null)
        } catch (err) {
            if (!isMountedRef.current) return
            setError(err?.message || "Failed to load exports.")
        } finally {
            if (isMountedRef.current) setLoading(false)
        }
    }, [endpointProvider, LINKS])

    // Initial fetch + 30s polling fallback (Socket.IO is primary).
    useEffect(() => {
        isMountedRef.current = true
        fetchExports()
        fetchStats()
        const interval = setInterval(() => {
            fetchExports()
            fetchStats()
        }, POLL_INTERVAL_MS)
        return () => {
            isMountedRef.current = false
            clearInterval(interval)
        }
    }, [fetchExports, fetchStats])

    // Mirror the pattern used by RMGatewayMonitoring: own the socket lifecycle
    // directly via raw io(), instead of going through SocketProvider. The
    // provider's getDataQuerySocket reference churns whenever an upstream
    // provider re-renders, which would tear down + re-establish subscriptions
    // and wipe live state. A modal-owned socket avoids that entirely — and
    // the server auto-cleans all rooms when the socket disconnects on unmount.
    const socketRef = useRef(null)
    const subscribedIdsRef = useRef(new Set())

    const tokenRef = useRef(token)
    const workspaceRef = useRef(selectedWorkspace)
    tokenRef.current = token
    workspaceRef.current = selectedWorkspace
    const queryUrlRef = useRef(LINKS.QUERY)
    queryUrlRef.current = LINKS.QUERY

    // Effect A: open a fresh socket on modal mount, attach listener, close on
    // unmount. Mount-only deps — see ref capture above.
    useEffect(() => {
        const socket = io(`${queryUrlRef.current}/jobs`, {
            transports: ["websocket"],
            auth: {
                token: tokenRef.current ?? "",
                workspace_name: workspaceRef.current ?? "",
            },
        })
        socketRef.current = socket

        const handleJobUpdate = (event) => {
            if (event?.error === "Job not found") return
            if (event?.error) {
                console.error("/jobs job_update error:", event.error)
                return
            }
            if (typeof event?.job_id !== "number") {
                console.warn("/jobs job_update missing job_id:", event)
                return
            }
            setItems(prev => prev.map(j => {
                if (Number(j.id) !== Number(event.job_id)) return j
                const merged = { ...j, ...event }
                const incoming = typeof event.progress === "number" ? event.progress : null
                const existing = typeof j.progress === "number" ? j.progress : null
                if (
                    event.status === "IN_PROGRESS"
                    && incoming !== null
                    && existing !== null
                    && incoming < existing
                ) {
                    merged.progress = existing
                }
                return merged
            }))
        }

        socket.on("job_update", handleJobUpdate)

        return () => {
            socket.disconnect()
            subscribedIdsRef.current.clear()
            socketRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Effect B: emit track/untrack diffs as items change. Listener stays put.
    useEffect(() => {
        const socket = socketRef.current
        if (!socket) return undefined

        const desired = new Set(items.map(j => String(j.id)))
        const subscribed = subscribedIdsRef.current

        desired.forEach(id => {
            if (!subscribed.has(id)) {
                socket.emit("track", id)
                subscribed.add(id)
            }
        })
        Array.from(subscribed).forEach(id => {
            if (!desired.has(id)) {
                socket.emit("untrack", id)
                subscribed.delete(id)
            }
        })
        return undefined
    }, [items])

    const recentsCount = Math.min(items.length, 10)
    const visibleItems = activeTab === "recents" ? items.slice(0, 10) : items

    return (
        <RemoteMonitoringModalContainer>
            <Header title="Export Tracker" onClose={close} />
            <RemoteMonitoringModalContent style={{ display: "flex", flexDirection: "column" }}>
                {error && (
                    <div style={{ color: "#d32f2f", padding: "12px 30px 0" }}>{error}</div>
                )}

                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingLeft: "30px",
                    paddingRight: "30px",
                    paddingTop: "10px",
                    paddingBottom: "16px",
                }}>
                    <Tabs
                        value={activeTab === "recents" ? 0 : 1}
                        onChange={(e, v) => setActiveTab(v === 0 ? "recents" : "all")}
                        aria-label="Export tracker tabs"
                        sx={{
                            "& .MuiTabs-indicator": {
                                backgroundColor: "var(--color-accent-interactive)",
                            },
                        }}
                    >
                        <Tab
                            label={`Recents (${recentsCount})`}
                            disableRipple
                            sx={{
                                textTransform: "none",
                                fontSize: MobergFontSize.LARGE,
                                borderRadius: "4px",
                                marginLeft: "-2px",
                                color: "var(--text-secondary-color)",
                                "&.Mui-selected": { color: "var(--color-accent-interactive)" },
                            }}
                        />
                        <Tab
                            label={`All (${items.length})`}
                            disableRipple
                            sx={{
                                textTransform: "none",
                                fontSize: MobergFontSize.LARGE,
                                borderRadius: "4px",
                                color: "var(--text-secondary-color)",
                                "&.Mui-selected": { color: "var(--color-accent-interactive)" },
                            }}
                        />
                    </Tabs>

                    <RefreshControls>
                        <ExportsStorageMeter stats={stats} />
                    </RefreshControls>
                </Box>

                <div style={{
                    position: "relative",
                    padding: "0 30px 20px",
                    minHeight: 500,
                    height: "100%",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflowX: "auto",
                    overflowY: "hidden",
                }}>
                    <ExportsListView
                        items={visibleItems}
                        setItems={setItems}
                        onRefresh={() => { fetchExports(); fetchStats(); }}
                        loading={loading}
                        emptyState={
                            <div style={{
                                textAlign: "center",
                                padding: 40,
                                color: "var(--text-secondary-color)",
                                fontStyle: "italic",
                            }}>
                                No exports yet. Start one from the Data Visualize page using the Actions menu.
                            </div>
                        }
                    />
                </div>

                <InfoBar>
                    <MdInfoOutline size={15} style={{ color: "#207dea", flexShrink: 0 }} />
                    <span>
                        In progress percentages update live and may briefly show 0% right
                        after reopening. They catch up within a couple seconds.
                    </span>
                </InfoBar>

            </RemoteMonitoringModalContent>
        </RemoteMonitoringModalContainer>
    )
}
